import { useMemo, useState } from 'react';
import { useShow, useNavigation } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Tag,
  Row,
  Col,
  Statistic,
  Button,
  Space,
  Popconfirm,
  message,
  Table,
  Progress,
  Tooltip,
} from 'antd';
import {
  DeleteOutlined,
  UserOutlined,
  DollarOutlined,
  CarOutlined,
  PlusOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { apiClient } from '../../lib/api-client';

interface Passenger {
  name: string;
  phone: string;
  passport?: string;
  seatNumber: number;
}

interface BusBooking {
  id: number;
  bookingCode: string;
  clientName: string;
  clientPhone: string;
  seatNumbers: number[];
  passengers: string | Passenger[];
  totalPrice: number;
  isPaid: boolean;
  status: string;
  createdAt: string;
}

interface BusLayout {
  id: number;
  name: string;
  totalSeats: number;
  rows: number;
  leftSeats: number;
  rightSeats: number;
  backRowSeats: number;
  hasBackRow: boolean;
  seatConfig?: number[][]; // Array of [row, col] positions
}

interface BusStationData {
  id: number;
  nameUz: string;
  nameRu: string;
  nameEn: string;
  address?: string;
}

interface BusTrip {
  id: number;
  busCompany: { id: number; name: string; contactPhone: string };
  departureRegion: { id: number; nameUz: string; nameRu: string; nameEn: string };
  departureCity: { id: number; nameUz: string; nameRu: string; nameEn: string };
  arrivalRegion: { id: number; nameUz: string; nameRu: string; nameEn: string };
  arrivalCity: { id: number; nameUz: string; nameRu: string; nameEn: string };
  departureBusStation: BusStationData | string | null;
  arrivalBusStation: BusStationData | string | null;
  departureTime: string;
  arrivalTime: string;
  pricePerSeat: number;
  totalSeats: number;
  availableSeats: number;
  bookedSeats: number[];
  busType: string;
  busNumber: string | null;
  status: string;
  description: string | null;
  amenities: string[];
  createdAt: string;
  bookings: BusBooking[];
  busLayout?: BusLayout;
  seatMap?: {
    total: number;
    booked: number[];
    available: number[];
  };
}

// Bus Diagram Component - supports both seatConfig and parameter-based layouts
const BusDiagram = ({
  totalSeats,
  rows,
  leftSeats,
  rightSeats,
  backRowSeats,
  hasBackRow,
  seatConfig,
  bookedSeats = [],
  onSeatClick,
}: {
  totalSeats: number;
  rows: number;
  leftSeats: number;
  rightSeats: number;
  backRowSeats: number;
  hasBackRow: boolean;
  seatConfig?: number[][];
  bookedSeats?: number[];
  onSeatClick?: (seatNumber: number) => void;
}) => {
  const seatSize = 36;
  const gap = 6;
  const aisleWidth = 24;

  // Generate seat layout from seatConfig or parameters
  const layout = useMemo(() => {
    // If seatConfig is provided, use it
    if (seatConfig && seatConfig.length > 0) {
      const sortedSeats = [...seatConfig].sort((a, b) => {
        if (a[0] !== b[0]) return a[0] - b[0];
        return a[1] - b[1];
      });
      return sortedSeats.map(([row, col], index) => ({
        number: index + 1,
        row,
        col,
        isWindow: col === 0 || col === 5, // Window seats at edges
      }));
    }

    // Fallback to parameter-based generation
    const seats: { number: number; row: number; col: number; isWindow: boolean }[] = [];
    let seatNumber = 1;
    const seatsPerRow = leftSeats + rightSeats;
    const regularRows = hasBackRow ? Math.ceil((totalSeats - backRowSeats) / seatsPerRow) : Math.ceil(totalSeats / seatsPerRow);

    for (let row = 0; row < regularRows && seatNumber <= totalSeats; row++) {
      for (let col = 0; col < leftSeats && seatNumber <= totalSeats; col++) {
        if (!hasBackRow || seatNumber <= totalSeats - backRowSeats) {
          seats.push({ number: seatNumber++, row, col, isWindow: col === 0 });
        }
      }
      for (let col = 0; col < rightSeats && seatNumber <= totalSeats; col++) {
        if (!hasBackRow || seatNumber <= totalSeats - backRowSeats) {
          seats.push({ number: seatNumber++, row, col: leftSeats + col, isWindow: col === rightSeats - 1 });
        }
      }
    }
    if (hasBackRow) {
      const backRow = regularRows;
      for (let col = 0; col < backRowSeats && seatNumber <= totalSeats; col++) {
        seats.push({ number: seatNumber++, row: backRow, col, isWindow: col === 0 || col === backRowSeats - 1 });
      }
    }
    return seats;
  }, [totalSeats, leftSeats, rightSeats, backRowSeats, hasBackRow, seatConfig]);

  if (layout.length === 0) {
    return <div style={{ color: '#999', textAlign: 'center', padding: 20 }}>O'rindiqlar yo'q</div>;
  }

  const maxRow = Math.max(...layout.map(s => s.row));
  const maxCol = Math.max(...layout.map(s => s.col));
  const minCol = Math.min(...layout.map(s => s.col));
  const usingSeatConfig = seatConfig && seatConfig.length > 0;

  // Calculate dimensions based on layout type
  const totalWidth = usingSeatConfig
    ? (maxCol - minCol + 1) * (seatSize + gap) + 30 // With aisle space
    : (leftSeats + rightSeats) * (seatSize + gap) + aisleWidth;
  const totalHeight = (maxRow + 1) * (seatSize + gap) + 60;

  return (
    <div
      style={{
        width: totalWidth,
        height: totalHeight,
        background: '#f5f5f5',
        borderRadius: 12,
        padding: 12,
        position: 'relative',
      }}
    >
      {/* Driver area */}
      <div
        style={{
          position: 'absolute',
          top: 8,
          right: 12,
          width: seatSize,
          height: seatSize,
          background: '#1a1a2e',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CarOutlined style={{ color: '#fff', fontSize: 18 }} />
      </div>

      {/* Seats container */}
      <div style={{ marginTop: seatSize + gap + 8 }}>
        {layout.map((seat) => {
          const isBooked = bookedSeats.includes(seat.number);

          // Calculate position based on layout type
          let left: number;
          if (usingSeatConfig) {
            const isAfterAisle = seat.col > 2;
            left = (seat.col - minCol) * (seatSize + gap) + (isAfterAisle ? 20 : 0);
          } else {
            left = seat.col < leftSeats
              ? seat.col * (seatSize + gap)
              : (seat.col - leftSeats) * (seatSize + gap) + leftSeats * (seatSize + gap) + aisleWidth;

            // For back row, center the seats
            if (seat.row === maxRow && hasBackRow && !usingSeatConfig) {
              const backRowWidth = backRowSeats * (seatSize + gap) - gap;
              const offset = (totalWidth - 2 * 12 - backRowWidth) / 2;
              left = seat.col * (seatSize + gap) + offset - 12;
            }
          }

          return (
            <Tooltip key={seat.number} title={`O'rindiq ${seat.number}${isBooked ? ' (Band)' : ' (Bo\'sh — bosing buyurtma yaratish)'}`}>
              <div
                onClick={() => !isBooked && onSeatClick?.(seat.number)}
                style={{
                  position: 'absolute',
                  left: left + 12,
                  top: seat.row * (seatSize + gap) + seatSize + gap + 8 + 12,
                  width: seatSize,
                  height: seatSize,
                  background: isBooked ? '#ff4d4f' : '#00C4AD',
                  borderRadius: 6,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: isBooked ? 'not-allowed' : 'pointer',
                  border: seat.isWindow ? '2px solid #0099ff' : 'none',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'transform 0.15s',
                }}
                onMouseEnter={(e) => { if (!isBooked) e.currentTarget.style.transform = 'scale(1.1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
              >
                {seat.number}
              </div>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
};

// Helper to parse passengers from JSON string or return array
const parsePassengers = (passengers: string | Passenger[] | undefined): Passenger[] => {
  if (!passengers) return [];
  if (Array.isArray(passengers)) return passengers;
  try {
    return JSON.parse(passengers);
  } catch {
    return [];
  }
};

export const BusTripShow = () => {
  const { show, list } = useNavigation();
  const navigate = useNavigate();
  const { queryResult } = useShow<BusTrip>();
  const { data, isLoading } = queryResult;
  const trip = data?.data;
  const [pdfLoading, setPdfLoading] = useState(false);

  const handleSeatClick = (seatNumber: number) => {
    if (trip) {
      navigate(`/bus/bookings/create?tripId=${trip.id}&seat=${seatNumber}`);
    }
  };

  const handleCancel = async () => {
    try {
      await apiClient.delete(`/admin/bus/trips/${trip?.id}`);
      message.success('Reys bekor qilindi');
      list('admin/bus/trips');
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleDownloadPassengerList = () => {
    if (!trip) return;
    setPdfLoading(true);
    try {
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth(); // 210
      const pageHeight = pdf.internal.pageSize.getHeight(); // 297
      const margin = 8;
      const contentWidth = pageWidth - margin * 2;

      // Colors
      const darkColor: [number, number, number] = [26, 26, 46];
      const grayColor: [number, number, number] = [100, 100, 100];
      const headerBg: [number, number, number] = [0, 140, 137];

      let y = margin;

      // ========== HEADER SECTION ==========
      // Top header bar
      pdf.setFillColor(...headerBg);
      pdf.rect(margin, y, contentWidth, 28, 'F');

      // "MA'LUMOTNOMA" title
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text("MA'LUMOTNOMA", pageWidth / 2, y + 10, { align: 'center' });

      // Document number
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`#${trip.id}`, pageWidth / 2, y + 17, { align: 'center' });

      // Route name
      const routeName = `${trip.departureCity?.nameUz || ''} - ${trip.arrivalCity?.nameUz || ''}`;
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text(routeName, pageWidth / 2, y + 25, { align: 'center' });

      y += 32;

      // ========== INFO ROW ==========
      pdf.setFillColor(245, 247, 250);
      pdf.rect(margin, y, contentWidth, 22, 'F');

      // Flatten all passengers from all bookings
      const allPassengers: {
        name: string;
        phone: string;
        passport: string;
        seat: number;
        price: number;
        bookingCode: string;
        isPaid: boolean;
      }[] = [];

      (trip.bookings || [])
        .filter((b) => b.status !== 'CANCELLED')
        .forEach((booking) => {
          const passengers = parsePassengers(booking.passengers);
          if (passengers.length > 0) {
            passengers.forEach((p) => {
              allPassengers.push({
                name: p.name,
                phone: p.phone || booking.clientPhone,
                passport: p.passport || '-',
                seat: p.seatNumber,
                price: trip.pricePerSeat || 0,
                bookingCode: booking.bookingCode,
                isPaid: booking.isPaid,
              });
            });
          } else {
            // Fallback: use booking-level data if no parsed passengers
            (booking.seatNumbers || []).forEach((seat: number) => {
              allPassengers.push({
                name: booking.clientName,
                phone: booking.clientPhone,
                passport: '-',
                seat,
                price: trip.pricePerSeat || 0,
                bookingCode: booking.bookingCode,
                isPaid: booking.isPaid,
              });
            });
          }
        });

      // Sort by seat number
      allPassengers.sort((a, b) => a.seat - b.seat);

      const totalSum = allPassengers.reduce((sum, p) => sum + p.price, 0);
      const fmtCurrency = (v: number) => new Intl.NumberFormat('uz-UZ').format(v);

      // Info labels
      const depTime = new Date(trip.departureTime).toLocaleString('uz-UZ', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });

      const col1X = margin + 4;
      const col2X = margin + contentWidth * 0.35;
      const col3X = margin + contentWidth * 0.65;

      pdf.setTextColor(...grayColor);
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.text("Yo'lovchilar soni:", col1X, y + 5);
      pdf.text('Kompaniya:', col2X, y + 5);
      pdf.text('Avtobus raqami:', col3X, y + 5);

      pdf.setTextColor(...darkColor);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text(String(allPassengers.length), col1X, y + 11);
      pdf.text(trip.busCompany?.name || '-', col2X, y + 11);
      pdf.text(trip.busNumber || '-', col3X, y + 11);

      pdf.setTextColor(...grayColor);
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Summa:', col1X, y + 16);
      pdf.text("Reys vaqti:", col2X, y + 16);
      pdf.text("Yo'nalish:", col3X, y + 16);

      pdf.setTextColor(...darkColor);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${fmtCurrency(totalSum)} so'm`, col1X, y + 22);
      pdf.text(depTime, col2X, y + 22);
      pdf.text(routeName, col3X, y + 22);

      y += 26;

      // ========== PASSENGER TABLE ==========
      const tableBody = allPassengers.map((p, i) => [
        String(i + 1),
        p.name,
        String(p.seat),
        p.phone,
        p.passport,
        p.isPaid ? "To'langan" : "To'lanmagan",
        `${fmtCurrency(p.price)}`,
        p.bookingCode,
      ]);

      autoTable(pdf, {
        startY: y,
        margin: { left: margin, right: margin },
        tableWidth: contentWidth,
        head: [[
          '№',
          'F.I.Sh.',
          'Joy',
          'Telefon raqami',
          'Passport',
          "To'lov",
          'Chipta narxi',
          'Buyurtma kodi',
        ]],
        body: tableBody,
        theme: 'grid',
        styles: {
          fontSize: 7,
          cellPadding: 1.5,
          lineColor: [180, 180, 180],
          lineWidth: 0.2,
          textColor: darkColor,
          overflow: 'linebreak',
        },
        headStyles: {
          fillColor: headerBg,
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 7,
          cellPadding: 2,
          halign: 'center',
        },
        columnStyles: {
          0: { cellWidth: 8, halign: 'center' },   // №
          1: { cellWidth: 'auto' },                  // F.I.Sh. - takes remaining space
          2: { cellWidth: 10, halign: 'center' },    // Joy (seat)
          3: { cellWidth: 28 },                       // Phone
          4: { cellWidth: 22 },                       // Passport
          5: { cellWidth: 20, halign: 'center' },    // Payment
          6: { cellWidth: 22, halign: 'right' },     // Price
          7: { cellWidth: 22, halign: 'center' },    // Booking code
        },
        alternateRowStyles: {
          fillColor: [245, 248, 250],
        },
        didDrawPage: (data) => {
          // Footer on each page
          const pageNum = pdf.getNumberOfPages();
          pdf.setFontSize(7);
          pdf.setTextColor(...grayColor);
          pdf.setFont('helvetica', 'normal');
          pdf.text(
            `Sahifa ${data.pageNumber} / ${pageNum}`,
            pageWidth / 2,
            pageHeight - 5,
            { align: 'center' },
          );
        },
      });

      // ========== FOOTER SECTION (after table) ==========
      // Get the final Y position after autoTable
      const finalY = (pdf as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable?.finalY || y + 20;
      let footerY = finalY + 8;

      // Check if footer fits on current page, otherwise add a new page
      if (footerY + 35 > pageHeight - 10) {
        pdf.addPage();
        footerY = margin;
      }

      // Signature area
      pdf.setDrawColor(180, 180, 180);
      pdf.setLineWidth(0.3);

      const sigColWidth = contentWidth / 3;

      // Column 1: Texnik ko'rik
      pdf.setTextColor(...darkColor);
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'bold');
      pdf.text("Texnik ko'rik natijasi:", margin + 4, footerY + 4);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Tasdiqlandi', margin + 4, footerY + 9);
      pdf.line(margin + 4, footerY + 20, margin + sigColWidth - 4, footerY + 20);
      pdf.setFontSize(6);
      pdf.text('(imzo)', margin + sigColWidth / 2 - 5, footerY + 24);

      // Column 2: Tibbiy ko'rik
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'bold');
      pdf.text("Tibbiy ko'rik natijasi:", margin + sigColWidth + 4, footerY + 4);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Tasdiqlandi', margin + sigColWidth + 4, footerY + 9);
      pdf.line(margin + sigColWidth + 4, footerY + 20, margin + sigColWidth * 2 - 4, footerY + 20);
      pdf.setFontSize(6);
      pdf.text('(imzo)', margin + sigColWidth * 1.5 - 5, footerY + 24);

      // Column 3: Dispetcher
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Dispetcher:', margin + sigColWidth * 2 + 4, footerY + 4);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Tasdiqlandi', margin + sigColWidth * 2 + 4, footerY + 9);
      pdf.line(margin + sigColWidth * 2 + 4, footerY + 20, margin + contentWidth - 4, footerY + 20);
      pdf.setFontSize(6);
      pdf.text('(imzo)', margin + sigColWidth * 2.5 - 5, footerY + 24);

      // Date line
      footerY += 30;
      pdf.setTextColor(...grayColor);
      pdf.setFontSize(7);
      const printDate = new Date().toLocaleString('uz-UZ', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
      pdf.text(`Chop etilgan sana: ${printDate}`, margin + 4, footerY);
      pdf.text('safaro.uz', pageWidth - margin - 4, footerY, { align: 'right' });

      // Save
      const fileName = `reys-${trip.id}-yo'lovchilar-ro'yxati.pdf`;
      pdf.save(fileName);
      message.success("Yo'lovchilar ro'yxati yuklab olindi");
    } catch (error) {
      console.error('PDF generation error:', error);
      message.error('PDF yaratishda xatolik');
    } finally {
      setPdfLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('uz-UZ').format(value) + " so'm";
  };

  const formatDateTime = (date: string) => {
    return new Date(date).toLocaleString('uz-UZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStationName = (station: BusStationData | string | null | undefined): string => {
    if (!station) return '-';
    if (typeof station === 'string') return station;
    return station.nameUz || '-';
  };

  const statusColors: Record<string, string> = {
    SCHEDULED: 'blue',
    DEPARTED: 'orange',
    COMPLETED: 'green',
    CANCELLED: 'red',
  };

  const statusLabels: Record<string, string> = {
    SCHEDULED: 'Rejalashtirilgan',
    DEPARTED: "Yo'lda",
    COMPLETED: 'Yakunlangan',
    CANCELLED: 'Bekor qilingan',
  };

  const busTypeLabels: Record<string, string> = {
    STANDARD: 'Standart',
    VIP: 'VIP',
    SLEEPER: 'Sleeper',
  };

  const amenityLabels: Record<string, string> = {
    wifi: 'Wi-Fi',
    ac: 'Konditsioner',
    toilet: 'Hojatxona',
    tv: 'TV',
    usb: 'USB quvvatlash',
    water: 'Bepul suv',
    blanket: "Ko'rpa",
  };

  const bookingColumns = [
    {
      title: 'Kod',
      dataIndex: 'bookingCode',
      key: 'bookingCode',
      render: (code: string) => <Tag color="blue">{code}</Tag>,
    },
    {
      title: 'Mijoz',
      key: 'client',
      render: (_: unknown, record: BusBooking) => (
        <div>
          <div>{record.clientName}</div>
          <div style={{ color: '#7F8C8D', fontSize: 12 }}>{record.clientPhone}</div>
        </div>
      ),
    },
    {
      title: "O'rindiqlar",
      dataIndex: 'seatNumbers',
      key: 'seatNumbers',
      render: (seats: number[]) => seats?.join(', ') || '-',
    },
    {
      title: 'Summa',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      render: (price: number) => formatCurrency(price),
    },
    {
      title: "To'lov",
      dataIndex: 'isPaid',
      key: 'isPaid',
      render: (isPaid: boolean) => (
        <Tag color={isPaid ? 'green' : 'orange'}>
          {isPaid ? "To'langan" : "To'lanmagan"}
        </Tag>
      ),
    },
    {
      title: 'Holat',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const colors: Record<string, string> = {
          PENDING: 'orange',
          CONFIRMED: 'blue',
          CANCELLED: 'red',
          COMPLETED: 'green',
        };
        const labels: Record<string, string> = {
          PENDING: 'Kutilmoqda',
          CONFIRMED: 'Tasdiqlangan',
          CANCELLED: 'Bekor qilingan',
          COMPLETED: 'Yakunlangan',
        };
        return <Tag color={colors[status] || 'default'}>{labels[status] || status}</Tag>;
      },
    },
    {
      title: 'Sana',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('uz-UZ'),
    },
  ];

  const bookedCount = trip?.bookedSeats?.length || 0;
  const totalSeats = trip?.totalSeats || 0;
  const bookingPercentage = totalSeats > 0 ? Math.round((bookedCount / totalSeats) * 100) : 0;
  const totalRevenue = trip?.bookings?.reduce((sum, b) => sum + (b.isPaid ? b.totalPrice : 0), 0) || 0;

  return (
    <Show
      isLoading={isLoading}
      title="Avtobus reysi"
      headerButtons={
        <Space>
          <Button
            icon={<FilePdfOutlined />}
            onClick={handleDownloadPassengerList}
            loading={pdfLoading}
            disabled={!trip?.bookings?.length}
            style={{ background: '#008C89', borderColor: '#008C89', color: '#fff' }}
          >
            Yo'lovchilar ro'yxati (PDF)
          </Button>
          {trip?.status === 'SCHEDULED' && (
            <>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate(`/bus/bookings/create?tripId=${trip.id}`)}
              >
                Buyurtma yaratish
              </Button>
              <Popconfirm
                title="Reysni bekor qilasizmi?"
                description="Barcha buyurtmalar ham bekor qilinadi"
                onConfirm={handleCancel}
              >
                <Button danger icon={<DeleteOutlined />}>
                  Bekor qilish
                </Button>
              </Popconfirm>
            </>
          )}
          <Button onClick={() => list('admin/bus/trips')}>
            Orqaga
          </Button>
        </Space>
      }
    >
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={8}>
          <Card>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <Tag color={statusColors[trip?.status || 'SCHEDULED']} style={{ fontSize: 14 }}>
                {statusLabels[trip?.status || 'SCHEDULED']}
              </Tag>
            </div>

            <Descriptions column={1} size="small">
              <Descriptions.Item label="Kompaniya">
                <a onClick={() => show('admin/bus/companies', trip?.busCompany?.id || 0)}>
                  {trip?.busCompany?.name}
                </a>
              </Descriptions.Item>
              <Descriptions.Item label="Turi">
                <Tag color={trip?.busType === 'VIP' ? 'gold' : 'default'}>
                  {busTypeLabels[trip?.busType || 'STANDARD']}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Avtobus raqami">
                {trip?.busNumber || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Narx">
                {formatCurrency(trip?.pricePerSeat || 0)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Yo'nalish" style={{ marginTop: 24 }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: '#7F8C8D', fontSize: 12 }}>Jo'nash</div>
              <div style={{ fontWeight: 500 }}>
                {trip?.departureCity?.nameUz}, {trip?.departureRegion?.nameUz}
              </div>
              <div style={{ fontSize: 12 }}>{getStationName(trip?.departureBusStation)}</div>
              <div style={{ color: '#1890ff' }}>{formatDateTime(trip?.departureTime || '')}</div>
            </div>
            <div>
              <div style={{ color: '#7F8C8D', fontSize: 12 }}>Yetib borish</div>
              <div style={{ fontWeight: 500 }}>
                {trip?.arrivalCity?.nameUz}, {trip?.arrivalRegion?.nameUz}
              </div>
              <div style={{ fontSize: 12 }}>{getStationName(trip?.arrivalBusStation)}</div>
              <div style={{ color: '#52c41a' }}>{formatDateTime(trip?.arrivalTime || '')}</div>
            </div>
          </Card>

          <Card title="Statistika" style={{ marginTop: 24 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Statistic
                  title="Buyurtmalar"
                  value={trip?.bookings?.length || 0}
                  prefix={<UserOutlined />}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Daromad"
                  value={formatCurrency(totalRevenue)}
                  prefix={<DollarOutlined />}
                />
              </Col>
            </Row>
            <div style={{ marginTop: 16 }}>
              <div style={{ marginBottom: 8 }}>
                O'rindiqlar: {bookedCount}/{totalSeats}
              </div>
              <Progress
                percent={bookingPercentage}
                status={bookingPercentage === 100 ? 'success' : 'active'}
              />
            </div>
          </Card>

        </Col>

        <Col xs={24} lg={16}>
          <Card title="Buyurtmalar">
            <Table
              dataSource={trip?.bookings || []}
              columns={bookingColumns}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              size="small"
              onRow={(record) => ({
                onClick: () => show('admin/bus/bookings', record.id),
                style: { cursor: 'pointer' },
              })}
            />
          </Card>

          <Card
            title="O'rindiqlar sxemasi"
            style={{ marginTop: 24 }}
            extra={
              trip?.busLayout && (
                <Tag color="blue">{trip.busLayout.name}</Tag>
              )
            }
          >
            <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
              <BusDiagram
                totalSeats={trip?.totalSeats || 45}
                rows={trip?.busLayout?.rows || 10}
                leftSeats={trip?.busLayout?.leftSeats || 2}
                rightSeats={trip?.busLayout?.rightSeats || 2}
                backRowSeats={trip?.busLayout?.backRowSeats || 5}
                hasBackRow={trip?.busLayout?.hasBackRow ?? true}
                seatConfig={trip?.busLayout?.seatConfig}
                bookedSeats={trip?.bookedSeats || []}
                onSeatClick={handleSeatClick}
              />
            </div>
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center', gap: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 20,
                  height: 20,
                  background: '#00C4AD',
                  borderRadius: 4,
                }} />
                <span>Bo'sh</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 20,
                  height: 20,
                  background: '#ff4d4f',
                  borderRadius: 4,
                }} />
                <span>Band</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 20,
                  height: 20,
                  background: '#00C4AD',
                  borderRadius: 4,
                  border: '2px solid #0099ff',
                }} />
                <span>Deraza</span>
              </div>
            </div>
          </Card>

          {trip?.amenities && trip.amenities.length > 0 && (
            <Card title="Qulayliklar" style={{ marginTop: 24 }}>
              <Space wrap>
                {trip.amenities.map((amenity) => (
                  <Tag key={amenity}>{amenityLabels[amenity] || amenity}</Tag>
                ))}
              </Space>
            </Card>
          )}

          {trip?.description && (
            <Card title="Tavsif" style={{ marginTop: 24 }}>
              <p>{trip.description}</p>
            </Card>
          )}
        </Col>
      </Row>
    </Show>
  );
};
